import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { distinctUntilChanged, skip, Subscription, finalize, forkJoin } from 'rxjs';
import {
  ConfirmActionModalComponent,
  DateTimeFieldComponent,
  DeviceFilePreviewUtil,
  DevicesLifecycleEventFormComponent,
  DevicesLifecycleModalTemplateComponent,
  DevicesProfileModalContainerComponent,
  DevicesProfileModalTemplateComponent,
  DevicesTableComponent,
  DevicesTableItem,
  DevicesPageTemplateComponent,
  ErrorBlockComponent,
  FloatingActionFabComponent,
  ListFilterShortcutDirective,
  ModalKeyboardDirective,
  PageActionModalComponent,
  PageContextGuardComponent,
  PagedFeedComponent,
} from '@clinic-platform/ui-components';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { LoadingBlockComponent } from '../../../shared/ui/loading-block/loading-block.component';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { AuthService } from '../../../core/auth/auth.service';
import { UserRole } from '../../../core/auth/user-role.type';
import { APP_CONFIG } from '../../../core/config/app-config.token';
import { AppConfig } from '../../../core/config/app-config.interface';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../settings/data-access/settings.models';
import { DeviceTypesService } from '../../device-types/data-access/device-types.service';
import { DeviceTypeItem } from '../../device-types/data-access/device-types.models';
import { DeviceTypeQuickViewModalComponent } from '../../device-types/components/device-type-quick-view-modal.component';
import { ClinicQuickViewModalComponent } from '../../clinics/components/clinic-quick-view-modal.component';
import { DevicesService } from '../data-access/devices.service';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import {
  DeviceItem,
  DeviceLifecycleEventItem,
  DeviceLifecycleEventType,
  DeviceListSort,
  DevicePage,
  DevicePhoto,
  DeviceStatus,
} from '../data-access/devices.models';

@Component({
  selector: 'app-devices-page',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    DevicesLifecycleEventFormComponent,
    DevicesLifecycleModalTemplateComponent,
    DevicesProfileModalContainerComponent,
    DevicesProfileModalTemplateComponent,
    DevicesTableComponent,
    DevicesPageTemplateComponent,
    DeviceTypeQuickViewModalComponent,
    ClinicQuickViewModalComponent,
    FloatingActionFabComponent,
    PageActionModalComponent,
    PagedFeedComponent,
    CompanyContextRequiredComponent,
    LoadingBlockComponent,
    ConfirmActionModalComponent,
    DateTimeFieldComponent,
    ErrorBlockComponent,
    ListFilterShortcutDirective,
    ModalKeyboardDirective,
    PageContextGuardComponent,
  ],
  template: `
    <lib-devices-page-template
      libListFilterShortcut
      [libListFilterShortcut]="hasRequiredSystemAdminFilters"
      (libListFilterShortcutOpen)="openFiltersModal()"
      title="Devices"
      subtitle="Company device inventory management"
    >
      <div libDevicesSecondary>
        @if (actionErrorText) {
          <lib-error-block variant="error" [message]="(actionErrorText | t) ?? ''" />
        }
        @if (successText) {
          <lib-error-block variant="success" [message]="successText" />
        }

        @if (isSystemAdmin) {
          <lib-page-context-guard [blocked]="!!systemAdminHeaderScopeReason">
            <app-company-context-required [message]="systemAdminHeaderScopeReason || ''" />
          </lib-page-context-guard>
        }
      </div>

      <div libDevicesBody>
        <lib-paged-feed
          [items]="visibleItems"
          [collectionTemplate]="devicesTableTemplate"
          [loading]="isLoading"
          [loadingMore]="isLoadingMore"
          [hasMore]="hasMoreItems"
          [errorText]="errorText"
          emptyText="No devices available yet."
          loadMoreText="Load more devices"
          loadingMoreText="Loading more devices..."
          retryText="Retry"
          (loadMore)="loadMoreDevices()"
          (retry)="refresh()"
        />

        <ng-template #devicesTableTemplate let-items="items">
          <lib-devices-table
            [items]="items"
            [clinicNameResolver]="clinicName.bind(this)"
            [deviceTypeNameResolver]="deviceTypeName.bind(this)"
            [showPhotosColumn]="true"
            [canOpenClinic]="true"
            [canOpenDeviceType]="true"
            actionLabel="Lifecycle"
            (rowSelected)="onDeviceRowSelected($event)"
            (actionClicked)="onDeviceLifecycleClicked($event)"
            (clinicOpenClicked)="openClinicQuickView($event)"
            (deviceTypeOpenClicked)="openDeviceTypeQuickView($event)"
          />
        </ng-template>
      </div>
    </lib-devices-page-template>

    @if (isClinicQuickViewOpen && selectedQuickViewClinicId) {
      <app-clinic-quick-view-modal
        [isOpen]="true"
        [clinicId]="selectedQuickViewClinicId"
        [companyIdOverride]="profileModalCompanyId"
        title="Clinic Quick View"
        (close)="closeClinicQuickView()"
      />
    }

    @if (isDeviceTypeQuickViewOpen && selectedQuickViewDeviceTypeId) {
      <app-device-type-quick-view-modal
        [isOpen]="true"
        [deviceTypeId]="selectedQuickViewDeviceTypeId"
        [companyIdOverride]="profileModalCompanyId"
        title="Device Type Quick View"
        (close)="closeDeviceTypeQuickView()"
      />
    }

    @if (isFiltersModalOpen) {
      <lib-page-action-modal
        [isOpen]="true"
        title="Filters & Sort"
        subtitle="Tune the device list without loading unrelated catalogs."
        width="620px"
        (close)="closeFiltersModal()"
      >
        <div class="devices-page__filters">
          <label>
            Status
            <select [ngModel]="draftStatus" (ngModelChange)="onDraftStatusValueChange($event)" [ngModelOptions]="{ standalone: true }">
              <option value="">All</option>
              @for (status of statuses; track status) {
                <option [value]="status">{{ status }}</option>
              }
            </select>
          </label>

          <label>
            Clinic
            <select [(ngModel)]="draftClinicId" [ngModelOptions]="{ standalone: true }">
              <option value="">All</option>
              @for (clinic of clinics; track clinic.clinicId) {
                <option [value]="clinic.clinicId">{{ clinic.clinicName }}</option>
              }
            </select>
          </label>

          <label>
            Device Type
            <select [(ngModel)]="draftDeviceTypeId" [ngModelOptions]="{ standalone: true }">
              <option value="">All</option>
              @for (deviceType of deviceTypes; track deviceType.id) {
                <option [value]="deviceType.id">{{ deviceType.name }}</option>
              }
            </select>
          </label>

          <label>
            Search
            <input
              [(ngModel)]="draftQuery"
              [ngModelOptions]="{ standalone: true }"
              placeholder="Brand, model, serial, clinic, type"
              data-modal-primary-focus="true"
            />
          </label>

          <label>
            Sort
            <select [ngModel]="draftSort" (ngModelChange)="onDraftSortValueChange($event)" [ngModelOptions]="{ standalone: true }">
              <option value="createdAtDesc">Newest created</option>
              <option value="createdAtAsc">Oldest created</option>
              <option value="updatedAtDesc">Newest updated</option>
              <option value="updatedAtAsc">Oldest updated</option>
              <option value="brandAsc">Brand A-Z</option>
              <option value="brandDesc">Brand Z-A</option>
              <option value="modelAsc">Model A-Z</option>
              <option value="modelDesc">Model Z-A</option>
              <option value="statusAsc">Status A-Z</option>
              <option value="statusDesc">Status Z-A</option>
            </select>
          </label>
        </div>

        <div class="devices-page__modal-actions">
          <button type="button" class="devices-page__action" (click)="resetFiltersModal()">Reset</button>
          <button type="button" class="devices-page__action" (click)="applyFiltersModal()">Apply</button>
        </div>
      </lib-page-action-modal>
    }

    <ng-template #pageActionsMenu let-close="close">
      <button
        type="button"
        role="menuitem"
        class="action-menu__item"
        [disabled]="!canOpenCreateModal()"
        (click)="openCreateModal(); close()"
      >
        Create device
      </button>
      <button type="button" role="menuitem" class="action-menu__item" (click)="openFiltersModal(); close()">
        Filters & Sort (Ctrl+F)
      </button>
      <button type="button" role="menuitem" class="action-menu__item" (click)="refresh(); close()">
        Refresh
      </button>
    </ng-template>

    <lib-floating-action-fab
      [menuTemplate]="pageActionsMenu"
      buttonAriaLabel="Open device actions"
    />

    @if (isDeviceModalOpen) {
      @if (isDeviceModalViewMode && editingItem; as viewedItem) {
        <lib-devices-profile-modal-container
          [isOpen]="true"
          [apiBaseUrl]="apiBaseUrl"
          [deviceId]="viewedItem.id"
          [companyId]="profileModalCompanyId"
          [role]="currentRole"
          [title]="deviceModalTitle"
          closeText="Cancel"
          [showCompanyId]="isSystemAdmin"
          [canArchive]="canArchiveDevices && viewedItem.status !== 'retired'"
          [canDelete]="canDeleteDevices"
          [canEdit]="canStartDeviceEdit"
          [actionsDisabled]="isArchivingDevice || isDeletingDevice || isDeviceModalBusy"
          (close)="closeDeviceModal()"
          (archive)="requestArchiveDevice(viewedItem)"
          (delete)="requestDeleteDevice(viewedItem)"
          (edit)="startDeviceEdit()"
        />
      } @else {
        <lib-devices-profile-modal-template
          [title]="deviceModalTitle"
          closeText="Cancel"
          [useDefaultLayout]="false"
          [showHeaderClose]="false"
          [canCancel]="!isDeviceModalBusy"
          [canSubmit]="canSubmitDeviceModal()"
          keyboardSubmitMode="submit"
          (close)="closeDeviceModal()"
          (submit)="submitDeviceModal()"
        >
          <div class="devices-page__modal">
            @if (deviceModalErrorText) {
              <div class="devices-page__error">{{ deviceModalErrorText | t }}</div>
            }
            <form class="devices-page__modal-form" [formGroup]="isDeviceModalCreateMode ? createForm : editForm" (ngSubmit)="submitDeviceModal()">
            @if (!isSystemAdmin) {
              <label>
                Clinic
                <select formControlName="clinicId" data-modal-primary-focus="true" [disabled]="isDeviceModalReadOnly">
                  <option value="">Select clinic</option>
                  @for (clinic of clinics; track clinic.clinicId) {
                    <option [value]="clinic.clinicId">{{ clinic.clinicName }}</option>
                  }
                </select>
              </label>
            }
            <label>
              Device Type
              <input
                type="text"
                formControlName="deviceTypeValue"
                placeholder="Select from list or type custom value"
                list="device-type-options-modal"
                [attr.data-modal-primary-focus]="isSystemAdmin ? 'true' : null"
                [disabled]="isSystemAdmin && !hasCompanyScopeSelected"
                [readonly]="isDeviceModalReadOnly"
              />
              <datalist id="device-type-options-modal">
                @for (item of (!isDeviceModalCreateMode ? filteredEditDeviceTypeOptions : filteredCreateDeviceTypeOptions); track item.id) {
                  <option [value]="item.name"></option>
                }
              </datalist>
            </label>
            <label>
              Serial Number
              <input type="text" formControlName="serialNumber" [readonly]="isDeviceModalReadOnly" />
            </label>
            <label>
              Brand
              <input type="text" formControlName="brand" [readonly]="isDeviceModalReadOnly" />
            </label>
            <label>
              Model
              <input type="text" formControlName="model" [readonly]="isDeviceModalReadOnly" />
            </label>
            <label>
              Manufacture Date (text)
              <input type="text" formControlName="manufactureDateText" [readonly]="isDeviceModalReadOnly" />
            </label>
            <label>
              Last Service Date (text)
              <input type="text" formControlName="lastServiceDateText" [readonly]="isDeviceModalReadOnly" />
            </label>
            <label>
              Added Date (text)
              <input type="text" formControlName="addedDateText" [readonly]="isDeviceModalReadOnly" />
            </label>
            <label class="devices-page__wide">
              Notes
              <textarea formControlName="notes" [readonly]="isDeviceModalReadOnly"></textarea>
            </label>
            @if (!isDeviceModalCreateMode) {
              <label>
                Status
                <select formControlName="status" [disabled]="isDeviceModalReadOnly">
                  @for (status of statuses; track status) {
                    <option [value]="status">{{ status }}</option>
                  }
                </select>
              </label>
            }

            @if (isDeviceModalCreateMode) {
              <label class="devices-page__wide">
                Photos
                <input type="file" accept="image/*" multiple (change)="onCreateFilesSelected($event)" />
              </label>
              @if (createPhotoFiles.length > 0) {
                <div class="devices-page__wide devices-page__file-hint">
                  Selected files: {{ createPhotoFiles.length }}
                </div>
              }
            } @else if (editingItem; as currentEditItem) {
              <div class="devices-page__photo-section">
                <div class="devices-page__photo-head">Photos</div>

                @if (currentEditItem.photos.length === 0) {
                  <div class="devices-page__photo-empty">No photos uploaded yet.</div>
                } @else {
                  <div class="devices-page__photo-grid">
                    @for (photo of currentEditItem.photos; track photo.key) {
                      <div class="devices-page__photo-item">
                        <img
                          [src]="photo.url"
                          alt="Device photo"
                          class="devices-page__photo-image"
                          (click)="openPhotoPreview(photo, $event)"
                        />
                        <button
                          type="button"
                          class="devices-page__action"
                          [disabled]="isUploadingPhotos || isDeviceModalReadOnly"
                          (click)="requestRemovePhoto(photo)"
                        >
                          Remove
                        </button>
                      </div>
                    }
                  </div>
                }

                @if (!isDeviceModalReadOnly) {
                  <label>
                    Upload more photos
                    <input type="file" accept="image/*" multiple (change)="onEditFilesSelected($event)" />
                  </label>
                  @if (editPhotoFiles.length > 0) {
                    <div class="devices-page__file-hint">Selected files: {{ editPhotoFiles.length }}</div>
                    <button
                      type="button"
                      class="devices-page__action"
                      [disabled]="isUploadingPhotos"
                      (click)="uploadEditPhotos()"
                    >
                      {{ isUploadingPhotos ? 'Uploading...' : 'Upload selected photos' }}
                    </button>
                  }
                }
              </div>
            }

            <div class="devices-page__modal-actions">
              @if (!isDeviceModalCreateMode && editingItem) {
                @if (canArchiveDevices && editingItem.status !== 'retired') {
                  <button
                    type="button"
                    class="devices-page__action"
                    [disabled]="isArchivingDevice || isDeletingDevice || isDeviceModalBusy"
                    (click)="requestArchiveDevice(editingItem)"
                  >
                    Archive
                  </button>
                }
                @if (canDeleteDevices) {
                  <button
                    type="button"
                    class="devices-page__action"
                    [disabled]="isArchivingDevice || isDeletingDevice || isDeviceModalBusy"
                    (click)="requestDeleteDevice(editingItem)"
                  >
                    Delete
                  </button>
                }
              }
              <button type="button" class="devices-page__action" [disabled]="isDeviceModalBusy" (click)="closeDeviceModal()">Cancel</button>
              @if (isDeviceModalViewMode) {
                <button
                  type="button"
                  class="devices-page__action"
                  [disabled]="isDeviceModalBusy || !canStartDeviceEdit"
                  (click)="startDeviceEdit()"
                >
                  Edit
                </button>
              } @else {
                <button type="submit" class="devices-page__action" [disabled]="!canSubmitDeviceModal()">
                  {{ isDeviceModalEditMode ? (isSavingEdit ? 'Saving...' : 'Save') : (isCreating ? 'Creating...' : 'Create device') }}
                </button>
              }
            </div>
            @if (isDeviceModalCreateMode && createSubmitBlockedReason) {
              <div class="devices-page__inline-error">{{ createSubmitBlockedReason | t }}</div>
            }
            </form>
          </div>
        </lib-devices-profile-modal-template>
      }
    }

    @if (previewAttachment) {
      <div class="devices-page__preview-backdrop" (click)="closeAttachmentPreview()">
        <div
          class="devices-page__preview-content"
          (click)="$event.stopPropagation()"
          [libModalKeyboard]="true"
          [libModalKeyboardCanSubmit]="false"
          (libModalKeyboardCancel)="closeAttachmentPreview()"
        >
          <div class="devices-page__preview-head">
            <strong>{{ previewAttachment.name }}</strong>
            <div class="devices-page__preview-actions">
              <button type="button" class="devices-page__action" (click)="downloadPreviewAttachment()" [disabled]="isDownloadingPreview">
                {{ isDownloadingPreview ? 'Downloading...' : 'Download' }}
              </button>
              <button type="button" class="devices-page__action" (click)="closeAttachmentPreview()">Close</button>
            </div>
          </div>

          @if (previewErrorText) {
            <div class="devices-page__error">{{ previewErrorText }}</div>
          } @else if (previewAttachment.kind === 'image') {
            <img [src]="previewAttachment.url" alt="Device attachment preview" class="devices-page__preview-image" />
          } @else if (previewFrameUrl) {
            <iframe class="devices-page__preview-frame" [src]="previewFrameUrl"></iframe>
          } @else {
            <div class="devices-page__photo-empty">Preview is not available for this file type.</div>
          }
        </div>
      </div>
    }

    @if (lifecycleDevice) {
      <lib-devices-lifecycle-modal-template
        title="Device lifecycle"
        closeText="Close"
        [meta]="lifecycleModalMeta"
        [editable]="canManageLifecycleEvents || canCreateLifecycleEvents"
        [canSubmit]="false"
        [autofocus]="canManageLifecycleEvents || canCreateLifecycleEvents"
        (cancel)="closeLifecycle()"
      >

          @if (lifecycleErrorText) {
            <div class="devices-page__error">{{ lifecycleErrorText }}</div>
          }

          @if (canCreateLifecycleEvents) {
            <lib-devices-lifecycle-event-form
              [form]="lifecycleCreateForm"
              [lifecycleTypes]="lifecycleTypes"
              [isSubmitting]="isCreatingLifecycleEvent"
              [selectedFilesCount]="lifecycleFiles.length"
              eventTypeLabel="Event Type"
              performedAtLabel="Performed At"
              titleLabel="Title"
              notesLabel="Notes"
              attachFilesLabel="Attach files (images, TXT, PDF, DOC/DOCX, XLS/XLSX)"
              submitLabel="Add event"
              submittingLabel="Creating..."
              selectedFilesLabel="Selected files:"
              (submitForm)="createLifecycleEvent()"
              (filesSelected)="onLifecycleFilesSelected($event)"
            />
          } @else {
            <div class="devices-page__lifecycle-readonly">
              Lifecycle is read-only for this role.
            </div>
          }

          <div class="devices-page__lifecycle-list">
            @if (isLoadingLifecycle) {
              <app-loading-block text="Loading..." />
            } @else if (lifecycleEvents.length === 0) {
              <div class="devices-page__photo-empty">No lifecycle events yet.</div>
            } @else {
              @for (event of lifecycleEvents; track event.id) {
                <article class="devices-page__lifecycle-item">
                  <header>
                    <strong>{{ event.eventType }}</strong>
                    <span>{{ event.performedAt | date: 'dd.MM.yyyy HH:mm' }}</span>
                  </header>
                  @if (canManageLifecycleEvents) {
                    <div class="devices-page__lifecycle-actions">
                      <button
                        type="button"
                        class="devices-page__action"
                        (click)="openLifecycleEdit(event)"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        class="devices-page__action"
                        (click)="requestDeleteLifecycleEvent(event)"
                      >
                        Delete
                      </button>
                    </div>
                  }
                  @if (event.title) {
                    <div><strong>{{ event.title }}</strong></div>
                  }
                  @if (event.notes) {
                    <div class="devices-page__lifecycle-notes">{{ event.notes }}</div>
                  }
                  @if (event.attachments.length > 0) {
                    <div class="devices-page__lifecycle-files">
                      @for (attachment of event.attachments; track attachment.key) {
                        @if (isImageAttachment(attachment) && !failedImageAttachments.has(attachment.url)) {
                          <button
                            type="button"
                            class="devices-page__image-link devices-page__image-button"
                            [title]="attachment.name"
                            (click)="openAttachmentPreview(attachment, event.id, 'lifecycle', $event)"
                          >
                            <img
                              [src]="attachment.url"
                              alt="Lifecycle attachment"
                              class="devices-page__lifecycle-image"
                              (error)="onAttachmentImageError(attachment.url)"
                            />
                          </button>
                        } @else if (isInlineRenderableAttachment(attachment)) {
                          <button
                            type="button"
                            class="devices-page__doc-thumb"
                            [title]="attachment.name"
                            (click)="openAttachmentPreview(attachment, event.id, 'lifecycle', $event)"
                          >
                            @if (isPdfAttachment(attachment)) {
                              <div class="devices-page__pdf-thumb-wrap">
                                <iframe
                                  class="devices-page__pdf-thumb-frame"
                                  [src]="getAttachmentThumbFrameUrl(attachment)"
                                  scrolling="no"
                                  loading="lazy"
                                ></iframe>
                              </div>
                            } @else {
                              <iframe
                                class="devices-page__doc-frame"
                                [src]="getAttachmentThumbFrameUrl(attachment)"
                                scrolling="no"
                                loading="lazy"
                              ></iframe>
                            }
                            <span class="devices-page__doc-name">{{ attachment.name }}</span>
                          </button>
                        } @else {
                          <a [href]="attachment.url" target="_blank" rel="noreferrer">{{ attachment.name }}</a>
                        }
                      }
                    </div>
                  }
                </article>
              }
            }
          </div>
      </lib-devices-lifecycle-modal-template>
    }

    @if (editingLifecycleEventId) {
      <div class="devices-page__modal-backdrop" (click)="cancelLifecycleEdit()">
        <div
          class="devices-page__modal"
          (click)="$event.stopPropagation()"
          [libModalKeyboard]="true"
          (libModalKeyboardCancel)="cancelLifecycleEdit()"
          (libModalKeyboardSubmit)="saveLifecycleEdit()"
        >
          <h3>Edit lifecycle event</h3>

          @if (lifecycleErrorText) {
            <div class="devices-page__error">{{ lifecycleErrorText }}</div>
          }

          <form [formGroup]="lifecycleEditForm" (ngSubmit)="saveLifecycleEdit()">
            <div class="devices-page__lifecycle-form">
              <label>
                Event Type
                <select formControlName="eventType">
                  @for (type of lifecycleTypes; track type) {
                    <option [value]="type">{{ type }}</option>
                  }
                </select>
              </label>

              <label>
                Performed At
                <lib-date-time-field inputId="devices-lifecycle-edit-performedAt" formControlName="performedAt" />
              </label>

              <label class="devices-page__wide">
                Title
                <input type="text" formControlName="title" data-modal-primary-focus="true" />
              </label>

              <label class="devices-page__wide">
                Notes
                <textarea formControlName="notes"></textarea>
              </label>
            </div>

            <div class="devices-page__modal-actions">
              <button type="button" class="devices-page__action" (click)="cancelLifecycleEdit()">Cancel</button>
              <button type="submit" class="devices-page__action" [disabled]="isSavingLifecycleEdit || lifecycleEditForm.invalid">
                {{ isSavingLifecycleEdit ? 'Saving...' : 'Save' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    }

    <lib-confirm-action-modal
      [isOpen]="!!pendingPhotoRemove"
      title="Remove photo"
      message="This photo will be removed from device profile."
      confirmText="Remove"
      cancelText="Cancel"
      loadingText="Removing..."
      [isLoading]="isUploadingPhotos"
      (cancel)="cancelRemovePhoto()"
      (confirm)="confirmRemovePhoto()"
    />

    <lib-confirm-action-modal
      [isOpen]="!!pendingLifecycleDelete"
      title="Delete lifecycle event"
      message="This lifecycle event and its attachments will be deleted."
      confirmText="Delete"
      cancelText="Cancel"
      loadingText="Deleting..."
      [isLoading]="isSavingLifecycleEdit"
      (cancel)="cancelDeleteLifecycleEvent()"
      (confirm)="confirmDeleteLifecycleEvent()"
    />

    <lib-confirm-action-modal
      [isOpen]="!!pendingDeviceArchive"
      title="Archive device"
      message="Device status will be changed to retired."
      confirmText="Archive"
      cancelText="Cancel"
      loadingText="Archiving..."
      [isLoading]="isArchivingDevice"
      (cancel)="cancelArchiveDevice()"
      (confirm)="confirmArchiveDevice()"
    />

    <lib-confirm-action-modal
      [isOpen]="!!pendingDeviceDelete"
      title="Delete device"
      message="Device and all dependent records/files will be permanently deleted."
      confirmText="Delete"
      cancelText="Cancel"
      loadingText="Deleting..."
      [isLoading]="isDeletingDevice"
      (cancel)="cancelDeleteDevice()"
      (confirm)="confirmDeleteDevice()"
    />
  `,
  styles: [`
    .devices-page__toolbar,
    .devices-page__table-wrap { margin-top: 12px; }
    .devices-page__toolbar { display:flex; justify-content:flex-end; }
    .devices-page__filters { margin-top:12px; display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:12px; align-items:end; min-width: 0; }
    .devices-page__filters label { display:flex; flex-direction:column; gap:6px; font-size:13px; }
    .devices-page__filters select,
    .devices-page__filters input { width: 100%; height:38px; border:1px solid #d1d5db; border-radius:8px; padding:0 10px; box-sizing: border-box; background: #fff; }
    .devices-page__grid { display: grid; grid-template-columns: repeat(auto-fit,minmax(220px,1fr)); gap: 12px; align-items: end; }
    .devices-page__grid label { display:flex; flex-direction:column; gap:6px; font-size:13px; }
    .devices-page__grid input,.devices-page__grid select,.devices-page__modal input,.devices-page__modal select {
      width: 100%;
      min-width: 0;
      height: 38px;
      border:1px solid #d1d5db;
      border-radius:8px;
      padding:0 10px;
      box-sizing: border-box;
      background: #fff;
      color: #0f172a;
      font: inherit;
      font-weight: 500;
    }
    .devices-page__grid textarea,
    .devices-page__modal textarea {
      width: 100%;
      min-width: 0;
      min-height: 72px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 8px 10px;
      resize: vertical;
      box-sizing: border-box;
      background: #fff;
      color: #0f172a;
      font: inherit;
      font-weight: 500;
    }
    .devices-page__modal input[type="file"] {
      height: auto;
      min-height: 38px;
      padding: 7px 10px;
    }
    .devices-page__wide { grid-column: 1 / -1; }
    .devices-page__grid button,.devices-page__action { height: 38px; border:1px solid #d1d5db; border-radius:8px; background:#fff; padding:0 12px; cursor:pointer; }
    .devices-page__table { width:100%; border-collapse: collapse; min-width: 980px; }
    .devices-page__table th,.devices-page__table td { border-bottom:1px solid #f3f4f6; padding:10px; text-align:left; }
    .devices-page__table-row { cursor: pointer; }
    .devices-page__table-row:hover { background: #f8fafc; }
    .devices-page__id-meta { margin-top:2px; color:#64748b; font-size:11px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; word-break: break-all; line-height:1.25; white-space:normal; }
    .devices-page__error { margin-top: 12px; color:#b91c1c; }
    .devices-page__success { margin-top: 12px; color:#166534; }
    .devices-page__inline-error { margin-top: 6px; color:#b91c1c; font-size:12px; }
    .devices-page__modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); display:grid; place-items:center; z-index:1000; }
    .devices-page__modal {
      width: 100%;
      min-width: 0;
      display:grid;
      gap:14px;
      box-sizing: border-box;
    }
    .devices-page__modal-form {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      min-width: 0;
    }
    .devices-page__modal label {
      display:grid;
      gap:6px;
      min-width: 0;
      color:#475569;
      font-size:12px;
      font-weight:700;
      line-height:1.25;
    }
    .devices-page__modal label:focus-within {
      color: #1d4ed8;
    }
    .devices-page__modal :is(input, select, textarea, button):focus-visible,
    .devices-page__modal :is(input, select, textarea, button):focus,
    .devices-page__preview-content button:focus-visible,
    .devices-page__preview-content button:focus {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
    }
    .devices-page__modal button:focus-visible,
    .devices-page__modal button:focus,
    .devices-page__preview-content button:focus-visible,
    .devices-page__preview-content button:focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
      outline: none;
    }
    .devices-page__modal :is(input, select, textarea):focus-visible,
    .devices-page__modal :is(input, select, textarea):focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
      outline: none;
    }
    .devices-page__modal-actions { grid-column: 1 / -1; display:flex; justify-content:flex-end; gap:8px; margin-top:8px; flex-wrap: wrap; }
    .devices-page__file-hint { color:#4b5563; font-size:12px; }
    .devices-page__photo-section { margin-top: 8px; display: grid; gap: 8px; }
    .devices-page__photo-head { font-weight: 600; }
    .devices-page__photo-empty { color: #6b7280; font-size: 13px; }
    .devices-page__photo-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); gap:10px; }
    .devices-page__photo-item { border:1px solid #e5e7eb; border-radius:8px; padding:8px; display:grid; gap:8px; }
    .devices-page__photo-image { width:100%; height:120px; object-fit:cover; border-radius:6px; background:#f3f4f6; cursor: zoom-in; }
    .devices-page__preview-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.7); z-index: 1200; display: grid; place-items: center; padding: 12px; }
    .devices-page__preview-content { background: #fff; border-radius: 12px; padding: 12px; width: min(1000px, calc(100vw - 24px)); max-height: calc(100vh - 24px); display: grid; gap: 10px; }
    .devices-page__preview-head { display:flex; align-items:center; justify-content:space-between; gap:8px; }
    .devices-page__preview-actions { display:flex; align-items:center; gap:8px; }
    .devices-page__preview-image { width: 100%; max-height: calc(100vh - 120px); object-fit: contain; border-radius: 8px; background: #f3f4f6; }
    .devices-page__preview-frame { width:100%; min-height:70vh; border:1px solid #e5e7eb; border-radius:8px; background:#fff; }
    .devices-page__lifecycle-modal { width:min(920px,calc(100vw - 24px)); }
    .devices-page__lifecycle-modal--editable:focus-within {
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
    }
    .devices-page__lifecycle-head { display:flex; align-items:center; justify-content:space-between; gap:8px; }
    .devices-page__lifecycle-meta { color:#4b5563; font-size:12px; }
    .devices-page__lifecycle-form { display:grid; grid-template-columns: repeat(auto-fit,minmax(220px,1fr)); gap:10px; }
    .devices-page__lifecycle-list { display:grid; gap:10px; border:1px solid #e5e7eb; border-radius:10px; padding:10px; background:#f9fafb; }
    .devices-page__lifecycle-item { display:grid; gap:6px; background:#fff; border:1px solid #e5e7eb; border-radius:8px; padding:10px; }
    .devices-page__lifecycle-modal--editable .devices-page__lifecycle-item:focus-within {
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
    }
    .devices-page__lifecycle-item header { display:flex; align-items:center; justify-content:space-between; gap:8px; font-size:12px; color:#374151; }
    .devices-page__lifecycle-actions { display:flex; align-items:center; gap:8px; justify-content:flex-end; }
    .devices-page__lifecycle-modal--editable .devices-page__lifecycle-actions .devices-page__action:focus-visible,
    .devices-page__lifecycle-modal--editable .devices-page__lifecycle-actions .devices-page__action:focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
      outline: none;
    }
    .devices-page__lifecycle-modal--editable button:focus-visible,
    .devices-page__lifecycle-modal--editable button:focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
      outline: none;
    }
    .devices-page__lifecycle-notes { font-size:13px; color:#374151; white-space:pre-wrap; }
    .devices-page__lifecycle-files { display:grid; gap:6px; grid-template-columns: repeat(auto-fill,minmax(120px,1fr)); }
    .devices-page__lifecycle-files a { font-size:12px; color:#0369a1; word-break:break-all; }
    .devices-page__lifecycle-readonly { font-size: 13px; color: #4b5563; }
    .devices-page__image-link { display:block; border:1px solid #cbd5e1; border-radius:8px; overflow:hidden; background:#f8fafc; }
    .devices-page__image-button { width:100%; padding:0; cursor:pointer; }
    .devices-page__lifecycle-image { display:block; width:100%; height:120px; object-fit:cover; }
    .devices-page__doc-thumb { display:grid; gap:6px; justify-items:stretch; text-align:left; padding:6px; width:100%; border:1px solid #cbd5e1; border-radius:8px; background:#f8fafc; cursor:pointer; }
    .devices-page__doc-frame { width:100%; height:120px; border:0; border-radius:6px; background:#fff; pointer-events:none; overflow:hidden; scrollbar-width:none; }
    .devices-page__pdf-thumb-wrap { width:100%; height:120px; border-radius:6px; border:1px solid #e2e8f0; background:#fff; overflow:hidden; position:relative; }
    .devices-page__pdf-thumb-wrap::after { content:''; position:absolute; top:0; right:0; width:14px; height:100%; background:#fff; pointer-events:none; }
    .devices-page__pdf-thumb-frame { position:absolute; top:0; left:0; width:200%; height:240px; border:0; transform:scale(0.5); transform-origin:top left; pointer-events:none; overflow:hidden; scrollbar-width:none; }
    .devices-page__doc-name { display:block; font-size:11px; color:#334155; word-break:break-word; line-height:1.3; }

    @media (max-width: 900px) {
      .devices-page__toolbar {
        justify-content: stretch;
      }

      .devices-page__toolbar .devices-page__action,
      .devices-page__modal-actions .devices-page__action {
        width: 100%;
      }

      .devices-page__modal-actions,
      .devices-page__lifecycle-actions,
      .devices-page__preview-actions {
        flex-direction: column;
        align-items: stretch;
      }

      .devices-page__grid,
      .devices-page__modal-form,
      .devices-page__lifecycle-form {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 640px) {
      .devices-page__filters {
        grid-template-columns: 1fr;
      }

      .devices-page__modal-backdrop,
      .devices-page__preview-backdrop {
        padding: 8px;
        align-items: end;
      }

      .devices-page__modal,
      .devices-page__preview-content,
      .devices-page__lifecycle-modal {
        width: 100%;
        max-height: min(92dvh, calc(100dvh - 12px));
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }

      .devices-page__modal,
      .devices-page__preview-content {
        padding: 12px;
      }

      .devices-page__photo-grid,
      .devices-page__lifecycle-files {
        grid-template-columns: 1fr 1fr;
      }

      .devices-page__preview-frame {
        min-height: 52dvh;
      }

      .devices-page__lifecycle-item header {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  `],
})
export class DevicesPageComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly devicesService = inject(DevicesService);
  private readonly settingsService = inject(SettingsService);
  private readonly deviceTypesService = inject(DeviceTypesService);
  private readonly authService = inject(AuthService);
  private readonly appConfig = inject<AppConfig>(APP_CONFIG);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly subscriptions = new Subscription();
  private static readonly PAGE_SIZE = 20;

  protected isLoading = true;
  protected isLoadingMore = false;
  protected isCreating = false;
  protected isSavingEdit = false;
  protected isUploadingPhotos = false;
  protected errorText: string | null = null;
  protected actionErrorText: string | null = null;
  protected modalActionErrorText: string | null = null;
  protected successText: string | null = null;
  protected isCreateModalOpen = false;
  protected deviceModalMode: 'create' | 'view' | 'edit' = 'create';
  protected previewAttachment: {
    key: string;
    eventId: string | null;
    name: string;
    url: string;
    kind: 'image' | 'pdf' | 'txt' | 'other';
    scope: 'device-photo' | 'lifecycle';
    deviceId: string;
  } | null = null;
  protected previewFrameUrl: SafeResourceUrl | null = null;
  protected failedImageAttachments = new Set<string>();
  protected previewErrorText: string | null = null;
  protected isDownloadingPreview = false;

  protected get lifecycleModalMeta(): string {
    if (!this.lifecycleDevice) {
      return '';
    }

    return this.buildDeviceShortInfo(this.lifecycleDevice);
  }
  protected pendingPhotoRemove: DevicePhoto | null = null;

  protected items: DeviceItem[] = [];
  protected clinics: ClinicOperationalSettingsDto[] = [];
  protected deviceTypes: DeviceTypeItem[] = [];
  protected editingItem: DeviceItem | null = null;
  protected readonly statuses: DeviceStatus[] = ['active', 'maintenance', 'broken', 'retired'];
  protected readonly lifecycleTypes: DeviceLifecycleEventType[] = [
    'maintenance',
    'repair',
    'inspection',
    'incident',
    'note',
  ];
  protected createPhotoFiles: File[] = [];
  protected editPhotoFiles: File[] = [];
  protected lifecycleDevice: DeviceItem | null = null;
  protected lifecycleEvents: DeviceLifecycleEventItem[] = [];
  protected lifecycleFiles: File[] = [];
  protected isLoadingLifecycle = false;
  protected isCreatingLifecycleEvent = false;
  protected isSavingLifecycleEdit = false;
  protected lifecycleErrorText: string | null = null;
  protected canCreateLifecycleEvents = false;
  protected canManageLifecycleEvents = false;
  protected canArchiveDevices = false;
  protected canDeleteDevices = false;
  protected isSystemAdmin = false;
  protected editingLifecycleEventId: string | null = null;
  protected pendingLifecycleDelete: DeviceLifecycleEventItem | null = null;
  protected pendingDeviceArchive: DeviceItem | null = null;
  protected pendingDeviceDelete: DeviceItem | null = null;
  protected isArchivingDevice = false;
  protected isDeletingDevice = false;
  protected hasRequiredSystemAdminFilters = false;
  protected visibleItems: DeviceItem[] = [];
  protected targetCompanyId: string | null = null;
  protected nextCursor: string | null = null;
  protected hasMoreItems = false;
  protected isFiltersModalOpen = false;
  protected selectedStatus: '' | DeviceStatus = '';
  protected selectedClinicId = '';
  protected selectedDeviceTypeId = '';
  protected selectedQuery = '';
  protected selectedSort: DeviceListSort = 'createdAtDesc';
  protected isClinicQuickViewOpen = false;
  protected selectedQuickViewClinicId: string | null = null;
  protected isDeviceTypeQuickViewOpen = false;
  protected selectedQuickViewDeviceTypeId: string | null = null;
  protected draftStatus: '' | DeviceStatus = '';
  protected draftClinicId = '';
  protected draftDeviceTypeId = '';
  protected draftQuery = '';
  protected draftSort: DeviceListSort = 'createdAtDesc';

  private buildDeviceShortInfo(item: Pick<DeviceItem, 'brand' | 'model' | 'serialNumber'>): string {
    const brand = item.brand?.trim() || '—';
    const model = item.model?.trim() || '—';
    const serial = item.serialNumber?.trim() || '—';
    return `Device: ${brand} / ${model} / ${serial}`;
  }

  protected readonly createForm = this.fb.nonNullable.group({
    clinicId: ['', [Validators.required]],
    deviceTypeValue: ['', [Validators.required, Validators.pattern(/\S+/)]],
    serialNumber: ['', [Validators.required, Validators.pattern(/\S+/)]],
    brand: ['', [Validators.required, Validators.pattern(/\S+/)]],
    model: ['', [Validators.required, Validators.pattern(/\S+/)]],
    manufactureDateText: ['', [Validators.required, Validators.pattern(/\S+/)]],
    lastServiceDateText: ['', [Validators.required, Validators.pattern(/\S+/)]],
    addedDateText: ['', [Validators.required, Validators.pattern(/\S+/)]],
    notes: [''],
  });

  protected readonly editForm = this.fb.nonNullable.group({
    clinicId: ['', [Validators.required]],
    deviceTypeValue: [''],
    serialNumber: [''],
    brand: [''],
    model: [''],
    manufactureDateText: [''],
    lastServiceDateText: [''],
    addedDateText: [''],
    notes: [''],
    status: this.fb.nonNullable.control<DeviceStatus>('active', [Validators.required]),
  });

  protected readonly lifecycleCreateForm = this.fb.nonNullable.group({
    eventType: this.fb.nonNullable.control<DeviceLifecycleEventType>('maintenance', [Validators.required]),
    title: [''],
    notes: [''],
    performedAt: [''],
  });

  protected readonly lifecycleEditForm = this.fb.nonNullable.group({
    eventType: this.fb.nonNullable.control<DeviceLifecycleEventType>('maintenance', [Validators.required]),
    title: [''],
    notes: [''],
    performedAt: [''],
  });

  protected get isDeviceModalOpen(): boolean {
    return this.isCreateModalOpen || !!this.editingItem;
  }

  protected get isDeviceModalCreateMode(): boolean {
    return this.deviceModalMode === 'create';
  }

  protected get isDeviceModalViewMode(): boolean {
    return this.deviceModalMode === 'view';
  }

  protected get isDeviceModalEditMode(): boolean {
    return this.deviceModalMode === 'edit';
  }

  protected get isDeviceModalReadOnly(): boolean {
    return this.isDeviceModalViewMode;
  }

  protected get deviceModalTitle(): string {
    if (this.isDeviceModalCreateMode) {
      return 'Create device';
    }
    if (this.isDeviceModalEditMode) {
      return 'Edit device';
    }
    return 'Device profile';
  }

  protected get isDeviceModalBusy(): boolean {
    if (this.isDeviceModalCreateMode) {
      return this.isCreating;
    }
    if (this.isDeviceModalEditMode) {
      return this.isSavingEdit;
    }
    return false;
  }

  protected get deviceModalErrorText(): string | null {
    return this.isDeviceModalCreateMode ? this.actionErrorText : this.modalActionErrorText;
  }

  protected get canStartDeviceEdit(): boolean {
    return !!this.editingItem && this.canEditDevice(this.editingItem);
  }

  protected get apiBaseUrl(): string {
    return this.appConfig.apiBaseUrl;
  }

  protected get currentRole(): UserRole | null {
    return (this.authService.getCurrentRole() as UserRole | null) ?? null;
  }

  protected get profileModalCompanyId(): string | null {
    if (!this.isSystemAdmin) {
      return null;
    }

    const selected = this.targetCompanyId?.trim();
    if (selected) {
      return selected;
    }

    return this.systemAdminContext.getCompanyId()?.trim() ?? null;
  }

  ngOnInit(): void {
    const role = this.authService.getCurrentRole() as UserRole | null;
    this.isSystemAdmin = role === 'system_admin';
    this.canCreateLifecycleEvents = role === 'manager' || role === 'system_admin';
    this.canManageLifecycleEvents = role === 'system_admin';
    this.canArchiveDevices = role === 'company_admin' || role === 'system_admin';
    this.canDeleteDevices = role === 'system_admin';
    if (this.isSystemAdmin) {
      this.createForm.patchValue({ clinicId: this.getSystemAdminClinicIdSeed() }, { emitEvent: false });
      this.subscriptions.add(this.systemAdminContext.companyId$.pipe(
        distinctUntilChanged(),
        skip(1),
      ).subscribe((companyId) => {
        const normalized = companyId ?? '';
        const normalizedCurrent = this.targetCompanyId ?? '';
        if (normalizedCurrent !== normalized) {
          this.targetCompanyId = companyId;
          this.resetScopeDependentState();
          this.loadDevices();
        }
      }));
      this.subscriptions.add(this.systemAdminContext.clinicId$.subscribe((clinicId) => {
        const normalized = clinicId?.trim() ?? '';
        if (this.createForm.controls.clinicId.value !== normalized) {
          this.createForm.patchValue({ clinicId: normalized }, { emitEvent: false });
        }
      }));
    }
    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected create(): void {
    if (this.isCreating) {
      return;
    }

    if (this.isSystemAdmin && !this.hasSystemAdminContextCompanySelected) {
      this.actionErrorText = 'error.settingsCompanyContextRequired';
      return;
    }
    if (this.isSystemAdmin && !this.hasSystemAdminContextClinicSelected) {
      this.actionErrorText = 'Select clinic first.';
      return;
    }

    if (!this.isSystemAdmin && !this.createForm.controls.clinicId.value.trim()) {
      this.actionErrorText = 'Select clinic first.';
      return;
    }

    this.isCreating = true;
    this.actionErrorText = null;
    this.modalActionErrorText = null;
    this.successText = null;

    const payload = this.createForm.getRawValue();
    const clinicId = this.isSystemAdmin
      ? this.getSystemAdminClinicIdSeed()
      : payload.clinicId.trim();
    const deviceTypeValue = payload.deviceTypeValue.trim();
    const resolvedType = this.resolveDeviceTypePayload(deviceTypeValue);

    if (!resolvedType.deviceTypeId && !resolvedType.deviceTypeName) {
      this.isCreating = false;
      this.actionErrorText = 'Select device type or enter custom value.';
      return;
    }

    this.devicesService.create({
      clinicId,
      deviceTypeId: resolvedType.deviceTypeId,
      deviceTypeName: resolvedType.deviceTypeName,
      serialNumber: payload.serialNumber.trim() || null,
      brand: payload.brand.trim() || null,
      model: payload.model.trim() || null,
      manufactureDateText: payload.manufactureDateText.trim() || null,
      lastServiceDateText: payload.lastServiceDateText.trim() || null,
      addedDateText: payload.addedDateText.trim() || null,
      notes: payload.notes.trim() || null,
    }, this.getCompanyScopeQuery()).pipe(finalize(() => (this.isCreating = false)))
      .subscribe({
        next: (created) => {
          if (this.createPhotoFiles.length > 0) {
            this.uploadPhotos(created.id, this.createPhotoFiles, 'global', () => {
              this.successText = 'Device created with uploaded photos';
              this.isCreateModalOpen = false;
              this.afterCreateReset();
            });
            return;
          }

          this.successText = 'Device created';
          this.isCreateModalOpen = false;
          this.afterCreateReset();
        },
        error: (error) => {
          this.actionErrorText = this.resolveActionError(error);
        },
      });
  }

  protected openEdit(item: DeviceItem): void {
    if (!this.canEditDevice(item)) {
      this.actionErrorText = 'Only system_admin can edit archived devices';
      return;
    }

    this.editingItem = item;
    this.loadReferenceDataIfNeeded();
    this.deviceModalMode = 'edit';
    this.editPhotoFiles = [];
    this.pendingPhotoRemove = null;
    this.actionErrorText = null;
    this.modalActionErrorText = null;
    this.editForm.reset({
      clinicId: item.clinicId,
      deviceTypeValue: item.deviceTypeName || this.deviceTypeName(item.deviceTypeId),
      serialNumber: item.serialNumber ?? '',
      brand: item.brand ?? '',
      model: item.model ?? '',
      manufactureDateText: item.manufactureDateText ?? '',
      lastServiceDateText: item.lastServiceDateText ?? '',
      addedDateText: item.addedDateText ?? '',
      notes: item.notes ?? '',
      status: item.status,
    });
  }

  protected closeEdit(): void {
    this.editingItem = null;
    this.deviceModalMode = 'create';
    this.editPhotoFiles = [];
    this.pendingPhotoRemove = null;
    this.modalActionErrorText = null;
    if (this.previewAttachment?.scope === 'device-photo') {
      this.closeAttachmentPreview();
    }
  }

  protected openDevice(item: DeviceItem): void {
    this.editingItem = item;
    this.deviceModalMode = 'view';
    this.editPhotoFiles = [];
    this.pendingPhotoRemove = null;
    this.actionErrorText = null;
    this.modalActionErrorText = null;
    this.editForm.reset({
      clinicId: item.clinicId,
      deviceTypeValue: item.deviceTypeName || this.deviceTypeName(item.deviceTypeId),
      serialNumber: item.serialNumber ?? '',
      brand: item.brand ?? '',
      model: item.model ?? '',
      manufactureDateText: item.manufactureDateText ?? '',
      lastServiceDateText: item.lastServiceDateText ?? '',
      addedDateText: item.addedDateText ?? '',
      notes: item.notes ?? '',
      status: item.status,
    });
  }

  protected onDeviceRowSelected(item: DevicesTableItem): void {
    this.openDevice(item as DeviceItem);
  }

  protected onDeviceLifecycleClicked(item: DevicesTableItem): void {
    this.openLifecycle(item as DeviceItem);
  }

  protected openClinicQuickView(clinicId: string): void {
    const normalized = clinicId.trim();
    if (!normalized) {
      return;
    }

    this.selectedQuickViewClinicId = normalized;
    this.isClinicQuickViewOpen = true;
  }

  protected closeClinicQuickView(): void {
    this.isClinicQuickViewOpen = false;
    this.selectedQuickViewClinicId = null;
  }

  protected openDeviceTypeQuickView(deviceTypeId: string): void {
    const normalized = deviceTypeId.trim();
    if (!normalized) {
      return;
    }

    this.selectedQuickViewDeviceTypeId = normalized;
    this.isDeviceTypeQuickViewOpen = true;
  }

  protected closeDeviceTypeQuickView(): void {
    this.isDeviceTypeQuickViewOpen = false;
    this.selectedQuickViewDeviceTypeId = null;
  }

  protected startDeviceEdit(): void {
    if (!this.editingItem || !this.canEditDevice(this.editingItem)) {
      this.modalActionErrorText = 'Only system_admin can edit archived devices';
      return;
    }
    this.loadReferenceDataIfNeeded();
    this.deviceModalMode = 'edit';
    this.modalActionErrorText = null;
  }

  protected openFiltersModal(): void {
    this.draftStatus = this.selectedStatus;
    this.draftClinicId = this.selectedClinicId;
    this.draftDeviceTypeId = this.selectedDeviceTypeId;
    this.draftQuery = this.selectedQuery;
    this.draftSort = this.selectedSort;
    this.isFiltersModalOpen = true;
    this.loadReferenceDataIfNeeded();
  }

  protected closeFiltersModal(): void {
    this.isFiltersModalOpen = false;
  }

  protected applyFiltersModal(): void {
    this.selectedStatus = this.draftStatus;
    this.selectedClinicId = this.draftClinicId;
    this.selectedDeviceTypeId = this.draftDeviceTypeId;
    this.selectedQuery = this.draftQuery.trim();
    this.selectedSort = this.draftSort;
    this.closeFiltersModal();
    this.loadDevices();
  }

  protected resetFiltersModal(): void {
    this.draftStatus = '';
    this.draftClinicId = '';
    this.draftDeviceTypeId = '';
    this.draftQuery = '';
    this.draftSort = 'createdAtDesc';
    this.applyFiltersModal();
  }

  protected onDraftStatusValueChange(value: string): void {
    this.draftStatus = this.isDeviceStatus(value) ? value : '';
  }

  protected onDraftSortValueChange(value: string): void {
    this.draftSort = this.isDeviceSort(value) ? value : 'createdAtDesc';
  }

  protected saveEdit(): void {
    if (!this.editingItem || this.editForm.invalid) return;

    this.isSavingEdit = true;
    this.modalActionErrorText = null;
    this.successText = null;

    const value = this.editForm.getRawValue();
    const deviceTypeValue = value.deviceTypeValue.trim();
    const resolvedType = this.resolveDeviceTypePayload(deviceTypeValue);

    if (!resolvedType.deviceTypeId && !resolvedType.deviceTypeName) {
      this.isSavingEdit = false;
      this.modalActionErrorText = 'Select device type or enter custom value.';
      return;
    }

    this.devicesService.update(this.editingItem.id, {
      clinicId: value.clinicId,
      deviceTypeId: resolvedType.deviceTypeId,
      deviceTypeName: resolvedType.deviceTypeName,
      serialNumber: value.serialNumber.trim() || null,
      brand: value.brand.trim() || null,
      model: value.model.trim() || null,
      manufactureDateText: value.manufactureDateText.trim() || null,
      lastServiceDateText: value.lastServiceDateText.trim() || null,
      addedDateText: value.addedDateText.trim() || null,
      notes: value.notes.trim() || null,
      status: value.status,
    }, this.getCompanyScopeQuery()).pipe(finalize(() => (this.isSavingEdit = false)))
      .subscribe({
        next: (updated) => {
          if (this.editPhotoFiles.length > 0) {
            const files = [...this.editPhotoFiles];
            this.uploadPhotos(updated.id, files, 'modal', (uploaded) => {
              this.editPhotoFiles = [];
              if (uploaded) {
                this.successText = 'Device updated with uploaded photos';
                this.replaceItem(uploaded);
                this.editingItem = uploaded;
              } else {
                this.successText = 'Device updated';
                this.replaceItem(updated);
                this.editingItem = updated;
              }
              this.deviceModalMode = 'view';
            });
            return;
          }

          this.successText = 'Device updated';
          this.replaceItem(updated);
          this.editingItem = updated;
          this.deviceModalMode = 'view';
        },
        error: (error) => {
          this.modalActionErrorText = this.resolveActionError(error);
        },
      });
  }

  protected onCreateFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.createPhotoFiles = input.files ? Array.from(input.files) : [];
  }

  protected openCreateModal(): void {
    this.actionErrorText = null;
    this.modalActionErrorText = null;
    if (!this.canOpenCreateModal()) {
      return;
    }
    this.loadReferenceDataIfNeeded();
    this.deviceModalMode = 'create';
    this.editingItem = null;
    this.isCreateModalOpen = true;
  }

  protected closeCreateModal(): void {
    if (this.isCreating) {
      return;
    }
    this.isCreateModalOpen = false;
    this.deviceModalMode = 'create';
    this.actionErrorText = null;
  }

  protected closeDeviceModal(): void {
    if (this.isDeviceModalBusy) {
      return;
    }
    if (!this.isDeviceModalCreateMode) {
      this.closeEdit();
      return;
    }
    this.closeCreateModal();
  }

  protected submitDeviceModal(): void {
    if (this.isDeviceModalBusy) {
      return;
    }

    if (this.isDeviceModalViewMode) {
      return;
    }
    if (this.isDeviceModalEditMode) {
      this.saveEdit();
      return;
    }
    this.create();
  }

  protected onEditFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.editPhotoFiles = input.files ? Array.from(input.files) : [];
  }

  protected uploadEditPhotos(): void {
    if (!this.editingItem || this.editPhotoFiles.length === 0) {
      return;
    }

    this.uploadPhotos(this.editingItem.id, this.editPhotoFiles, 'modal', (updated) => {
      if (!updated) {
        return;
      }
      this.successText = 'Photos uploaded';
      this.editPhotoFiles = [];
      this.editingItem = updated;
      this.replaceItem(updated);
    });
  }

  protected requestRemovePhoto(photo: DevicePhoto): void {
    this.pendingPhotoRemove = photo;
  }

  protected cancelRemovePhoto(): void {
    if (this.isUploadingPhotos) {
      return;
    }

    this.pendingPhotoRemove = null;
  }

  protected confirmRemovePhoto(): void {
    if (!this.editingItem || !this.pendingPhotoRemove) {
      return;
    }

    this.isUploadingPhotos = true;
    this.modalActionErrorText = null;
    this.successText = null;

    this.devicesService.removePhoto(
      this.editingItem.id,
      this.pendingPhotoRemove.key,
      this.getCompanyScopeQuery(),
    )
      .pipe(finalize(() => (this.isUploadingPhotos = false)))
      .subscribe({
        next: (updated) => {
          this.successText = 'Photo removed';
          this.pendingPhotoRemove = null;
          this.editingItem = updated;
          this.replaceItem(updated);
        },
        error: (error) => {
          this.modalActionErrorText = this.resolveActionError(error);
        },
      });
  }

  protected openPhotoPreview(photo: DevicePhoto, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.previewAttachment = {
      key: photo.key,
      eventId: null,
      name: DeviceFilePreviewUtil.getFileName({ key: photo.key, url: photo.url }),
      url: photo.url,
      // Device photos are always images even if key/url has no explicit extension.
      kind: 'image',
      scope: 'device-photo',
      deviceId: this.editingItem?.id ?? '',
    };
    this.previewErrorText = null;
    this.previewFrameUrl = null;
  }

  protected onAttachmentImageError(url: string): void {
    this.failedImageAttachments.add(url);
  }

  protected isImageAttachment(attachment: { key: string; name: string; url: string }): boolean {
    return DeviceFilePreviewUtil.isImage(attachment);
  }

  protected isInlineRenderableAttachment(attachment: { key: string; name: string; url: string }): boolean {
    return DeviceFilePreviewUtil.isInlinePreview(attachment);
  }

  protected isPdfAttachment(attachment: { key: string; name: string; url: string }): boolean {
    return DeviceFilePreviewUtil.isPdf(attachment);
  }

  protected getAttachmentThumbFrameUrl(attachment: { key: string; name: string; url: string }): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      DeviceFilePreviewUtil.getThumbFrameUrl(attachment),
    );
  }

  protected openAttachmentPreview(
    attachment: { key: string; name: string; url: string },
    eventId: string | null,
    scope: 'device-photo' | 'lifecycle',
    event: MouseEvent,
  ): void {
    event.preventDefault();
    event.stopPropagation();

    const kind = DeviceFilePreviewUtil.getKind(attachment);
    this.previewAttachment = {
      key: attachment.key,
      eventId,
      name: attachment.name,
      url: attachment.url,
      kind,
      scope,
      deviceId: this.lifecycleDevice?.id ?? this.editingItem?.id ?? '',
    };
    this.previewErrorText = null;
    this.previewFrameUrl = null;

    if (kind === 'pdf' || kind === 'txt') {
      this.previewFrameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        attachment.url,
      );
    }
  }

  protected downloadPreviewAttachment(): void {
    if (!this.previewAttachment || !this.previewAttachment.deviceId) {
      return;
    }

    this.previewErrorText = null;
    this.isDownloadingPreview = true;

    const fileName = this.previewAttachment.name;
    const onSuccess = (url: string) => {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.rel = 'noreferrer';
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      this.isDownloadingPreview = false;
    };

    if (this.previewAttachment.scope === 'device-photo') {
      this.devicesService.getDevicePhotoDownloadUrl(
        this.previewAttachment.deviceId,
        this.previewAttachment.key,
        this.getCompanyScopeQuery(),
      ).subscribe({
        next: ({ url }) => onSuccess(url),
        error: (error) => {
          this.previewErrorText = this.resolveActionError(error);
          this.isDownloadingPreview = false;
        },
      });
      return;
    }

    if (!this.previewAttachment.eventId) {
      this.previewErrorText = 'Attachment context is missing.';
      this.isDownloadingPreview = false;
      return;
    }

    this.devicesService.getLifecycleAttachmentDownloadUrl(
      this.previewAttachment.deviceId,
      this.previewAttachment.eventId,
      this.previewAttachment.key,
      this.getCompanyScopeQuery(),
    ).subscribe({
      next: ({ url }) => onSuccess(url),
      error: (error) => {
        this.previewErrorText = this.resolveActionError(error);
        this.isDownloadingPreview = false;
      },
    });
  }

  protected closeAttachmentPreview(): void {
    this.previewAttachment = null;
    this.previewFrameUrl = null;
    this.previewErrorText = null;
    this.isDownloadingPreview = false;
  }

  protected openLifecycle(item: DeviceItem): void {
    this.lifecycleDevice = item;
    this.lifecycleEvents = [];
    this.lifecycleFiles = [];
    this.failedImageAttachments.clear();
    this.lifecycleErrorText = null;
    this.lifecycleCreateForm.reset({
      eventType: 'maintenance',
      title: '',
      notes: '',
      performedAt: '',
    });
    this.loadLifecycleEvents();
  }

  protected closeLifecycle(): void {
    this.lifecycleDevice = null;
    this.lifecycleEvents = [];
    this.lifecycleFiles = [];
    this.lifecycleErrorText = null;
    this.editingLifecycleEventId = null;
    this.pendingLifecycleDelete = null;
    this.isLoadingLifecycle = false;
    this.isCreatingLifecycleEvent = false;
    this.isSavingLifecycleEdit = false;
    this.failedImageAttachments.clear();
    if (this.previewAttachment?.scope === 'lifecycle') {
      this.closeAttachmentPreview();
    }
  }

  protected onLifecycleFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.lifecycleFiles = input.files ? Array.from(input.files) : [];
  }

  protected createLifecycleEvent(): void {
    if (
      this.isCreatingLifecycleEvent
      || !this.canCreateLifecycleEvents
      || !this.lifecycleDevice
      || this.lifecycleCreateForm.invalid
    ) {
      return;
    }

    const deviceId = this.lifecycleDevice.id;
    const value = this.lifecycleCreateForm.getRawValue();
    const title = value.title?.trim() ?? '';
    const notes = value.notes?.trim() ?? '';

    if (!title || !notes) {
      this.lifecycleErrorText = 'Title and Notes are required.';
      return;
    }

    this.lifecycleErrorText = null;
    this.isCreatingLifecycleEvent = true;

    this.devicesService.createLifecycleEvent(deviceId, {
      eventType: value.eventType,
      title,
      notes,
      performedAt: value.performedAt?.trim() || undefined,
    }, this.getCompanyScopeQuery()).pipe(
      finalize(() => (this.isCreatingLifecycleEvent = false)),
    ).subscribe({
      next: (created) => {
        if (this.lifecycleFiles.length > 0) {
          const files = [...this.lifecycleFiles];
          this.isCreatingLifecycleEvent = true;
          this.devicesService.uploadLifecycleEventFiles(
            deviceId,
            created.id,
            files,
            this.getCompanyScopeQuery(),
          ).pipe(finalize(() => (this.isCreatingLifecycleEvent = false)))
            .subscribe({
              next: () => {
                this.lifecycleFiles = [];
                this.lifecycleCreateForm.patchValue({
                  eventType: 'maintenance',
                  title: '',
                  notes: '',
                  performedAt: '',
                });
                this.loadLifecycleEvents();
              },
              error: (error) => {
                this.lifecycleErrorText = this.resolveActionError(error);
              },
            });
          return;
        }

        this.lifecycleCreateForm.patchValue({
          eventType: 'maintenance',
          title: '',
          notes: '',
          performedAt: '',
        });
        this.loadLifecycleEvents();
      },
      error: (error) => {
        this.lifecycleErrorText = this.resolveActionError(error);
      },
    });
  }

  protected openLifecycleEdit(item: DeviceLifecycleEventItem): void {
    if (!this.canManageLifecycleEvents) {
      return;
    }

    this.editingLifecycleEventId = item.id;
    this.lifecycleErrorText = null;
    this.lifecycleEditForm.reset({
      eventType: item.eventType,
      title: item.title ?? '',
      notes: item.notes ?? '',
      performedAt: this.toDateTimeLocal(item.performedAt),
    });
  }

  protected cancelLifecycleEdit(): void {
    if (this.isSavingLifecycleEdit) {
      return;
    }

    this.editingLifecycleEventId = null;
  }

  protected saveLifecycleEdit(): void {
    if (
      !this.canManageLifecycleEvents
      || !this.lifecycleDevice
      || !this.editingLifecycleEventId
      || this.lifecycleEditForm.invalid
    ) {
      return;
    }

    const value = this.lifecycleEditForm.getRawValue();
    const title = value.title.trim();
    const notes = value.notes.trim();

    if (!title || !notes) {
      this.lifecycleErrorText = 'Title and Notes are required.';
      return;
    }

    this.isSavingLifecycleEdit = true;
    this.lifecycleErrorText = null;

    this.devicesService.updateLifecycleEvent(
      this.lifecycleDevice.id,
      this.editingLifecycleEventId,
      {
        eventType: value.eventType,
        title,
        notes,
        performedAt: value.performedAt.trim() || undefined,
      },
      this.getCompanyScopeQuery(),
    ).pipe(finalize(() => (this.isSavingLifecycleEdit = false)))
      .subscribe({
        next: () => {
          this.editingLifecycleEventId = null;
          this.loadLifecycleEvents();
        },
        error: (error) => {
          this.lifecycleErrorText = this.resolveActionError(error);
        },
      });
  }

  protected requestDeleteLifecycleEvent(item: DeviceLifecycleEventItem): void {
    if (!this.canManageLifecycleEvents || this.isSavingLifecycleEdit) {
      return;
    }

    this.pendingLifecycleDelete = item;
  }

  protected requestArchiveDevice(item: DeviceItem): void {
    if (!this.canArchiveDevices || this.isArchivingDevice || this.isDeletingDevice) {
      return;
    }

    this.pendingDeviceArchive = item;
  }

  protected cancelArchiveDevice(): void {
    if (this.isArchivingDevice) {
      return;
    }

    this.pendingDeviceArchive = null;
  }

  protected confirmArchiveDevice(): void {
    if (!this.canArchiveDevices || !this.pendingDeviceArchive) {
      return;
    }

    this.isArchivingDevice = true;
    this.actionErrorText = null;
    this.modalActionErrorText = null;
    this.successText = null;

    const target = this.pendingDeviceArchive;
    this.devicesService.archive(target.id, this.getCompanyScopeQuery())
      .pipe(finalize(() => (this.isArchivingDevice = false)))
      .subscribe({
        next: (updated) => {
          this.pendingDeviceArchive = null;
          this.replaceItem(updated);
          if (this.editingItem?.id === updated.id) {
            this.editingItem = updated;
          }
          if (this.lifecycleDevice?.id === updated.id) {
            this.lifecycleDevice = updated;
          }
          this.successText = 'Device archived';
        },
        error: (error) => {
          const message = this.resolveActionError(error);
          if (this.isDeviceModalOpen && !this.isDeviceModalCreateMode) {
            this.modalActionErrorText = message;
          } else {
            this.actionErrorText = message;
          }
        },
      });
  }

  protected requestDeleteDevice(item: DeviceItem): void {
    if (!this.canDeleteDevices || this.isDeletingDevice || this.isArchivingDevice) {
      return;
    }

    this.pendingDeviceDelete = item;
  }

  protected cancelDeleteDevice(): void {
    if (this.isDeletingDevice) {
      return;
    }

    this.pendingDeviceDelete = null;
  }

  protected confirmDeleteDevice(): void {
    if (!this.canDeleteDevices || !this.pendingDeviceDelete) {
      return;
    }

    this.isDeletingDevice = true;
    this.actionErrorText = null;
    this.modalActionErrorText = null;
    this.successText = null;

    const target = this.pendingDeviceDelete;
    this.devicesService.delete(target.id, this.getCompanyScopeQuery())
      .pipe(finalize(() => (this.isDeletingDevice = false)))
      .subscribe({
        next: () => {
          this.pendingDeviceDelete = null;
          this.items = this.items.filter((item) => item.id !== target.id);
          this.applyVisibleItems();

          if (this.editingItem?.id === target.id) {
            this.closeEdit();
          }

          if (this.lifecycleDevice?.id === target.id) {
            this.closeLifecycle();
          }

          if (this.previewAttachment?.deviceId === target.id) {
            this.closeAttachmentPreview();
          }

          this.successText = 'Device deleted';
        },
        error: (error) => {
          const message = this.resolveActionError(error);
          if (this.isDeviceModalOpen && !this.isDeviceModalCreateMode) {
            this.modalActionErrorText = message;
          } else {
            this.actionErrorText = message;
          }
        },
      });
  }

  protected cancelDeleteLifecycleEvent(): void {
    if (this.isSavingLifecycleEdit) {
      return;
    }

    this.pendingLifecycleDelete = null;
  }

  protected confirmDeleteLifecycleEvent(): void {
    if (!this.canManageLifecycleEvents || !this.lifecycleDevice || !this.pendingLifecycleDelete) {
      return;
    }

    this.isSavingLifecycleEdit = true;
    this.lifecycleErrorText = null;

    this.devicesService.deleteLifecycleEvent(
      this.lifecycleDevice.id,
      this.pendingLifecycleDelete.id,
      this.getCompanyScopeQuery(),
    ).pipe(finalize(() => (this.isSavingLifecycleEdit = false)))
      .subscribe({
        next: () => {
          this.pendingLifecycleDelete = null;
          if (this.editingLifecycleEventId) {
            this.editingLifecycleEventId = null;
          }
          this.loadLifecycleEvents();
        },
        error: (error) => {
          this.lifecycleErrorText = this.resolveActionError(error);
        },
      });
  }

  protected clinicName(clinicId: string): string {
    return this.clinics.find((item) => item.clinicId === clinicId)?.clinicName ?? clinicId;
  }

  protected deviceTypeName(deviceTypeId: string): string {
    return this.deviceTypes.find((item) => item.id === deviceTypeId)?.name ?? deviceTypeId;
  }

  protected canEditDevice(item: DeviceItem): boolean {
    if (item.status !== 'retired') {
      return true;
    }

    return this.isSystemAdmin;
  }

  private load(): void {
    this.targetCompanyId = this.isSystemAdmin
      ? this.systemAdminContext.getCompanyId()?.trim() || null
      : null;
    this.loadDevices();
  }

  protected refresh(): void {
    this.loadDevices();
  }

  protected loadMoreDevices(): void {
    if (!this.hasMoreItems || this.isLoadingMore) {
      return;
    }

    this.loadDevices({ append: true });
  }

  private loadDevices(options: { append: boolean } = { append: false }): void {
    if (this.isSystemAdmin && !this.getCompanyScopeQuery()) {
      this.items = [];
      this.visibleItems = [];
      this.nextCursor = null;
      this.hasMoreItems = false;
      this.errorText = null;
      this.actionErrorText = null;
      this.isLoading = false;
      this.isLoadingMore = false;
      return;
    }

    if (options.append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.errorText = null;
      this.actionErrorText = null;
      this.nextCursor = null;
      this.hasMoreItems = false;
    }

    this.devicesService.getPage({
      companyId: this.getCompanyScopeQuery(),
      limit: DevicesPageComponent.PAGE_SIZE,
      cursor: options.append ? this.nextCursor : null,
      sort: this.selectedSort,
      filters: {
        status: this.selectedStatus || undefined,
        clinicId: this.selectedClinicId || undefined,
        deviceTypeId: this.selectedDeviceTypeId || undefined,
        q: this.selectedQuery || undefined,
      },
    }).subscribe({
      next: (page: DevicePage) => {
        this.items = options.append ? [...this.items, ...page.items] : page.items;
        this.nextCursor = page.nextCursor;
        this.hasMoreItems = page.hasMore;
        this.applyVisibleItems();
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: (error) => {
        if (options.append) {
          this.actionErrorText = this.resolveActionError(error);
          this.isLoadingMore = false;
          return;
        }

        this.errorText = resolveHttpErrorMessageKey(error, { fallback: 'error.generic' });
        this.isLoading = false;
        this.isLoadingMore = false;
      },
    });
  }

  private loadLifecycleEvents(): void {
    if (!this.lifecycleDevice) {
      return;
    }

    this.isLoadingLifecycle = true;
    this.devicesService.getLifecycleEvents(this.lifecycleDevice.id, this.getCompanyScopeQuery())
      .pipe(finalize(() => (this.isLoadingLifecycle = false)))
      .subscribe({
        next: (items) => {
          this.lifecycleEvents = items;
          this.failedImageAttachments.clear();
        },
        error: (error) => {
          this.lifecycleErrorText = this.resolveActionError(error);
        },
      });
  }

  private uploadPhotos(
    deviceId: string,
    files: File[],
    errorScope: 'global' | 'modal',
    onSuccess: (updated?: DeviceItem) => void,
  ): void {
    this.isUploadingPhotos = true;
    this.devicesService.uploadPhotos(deviceId, files, this.getCompanyScopeQuery())
      .pipe(finalize(() => (this.isUploadingPhotos = false)))
      .subscribe({
        next: (updated) => {
          this.replaceItem(updated);
          onSuccess(updated);
        },
        error: (error) => {
          if (errorScope === 'modal') {
            this.modalActionErrorText = this.resolveActionError(error);
          } else {
            this.actionErrorText = this.resolveActionError(error);
          }
        },
      });
  }

  private resolveActionError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const payload = error.error;
      if (payload && typeof payload === 'object') {
        const message = (payload as { message?: unknown }).message;

        if (typeof message === 'string' && message.trim().length > 0) {
          return message;
        }

        if (Array.isArray(message) && message.length > 0) {
          const firstMessage = message.find((item) => typeof item === 'string');
          if (typeof firstMessage === 'string' && firstMessage.trim().length > 0) {
            return firstMessage;
          }
        }
      }
    }

    return resolveHttpErrorMessageKey(error, { fallback: 'error.generic' });
  }

  private replaceItem(updated: DeviceItem): void {
    if (!this.matchesSelectedFilters(updated)) {
      this.items = this.items.filter((item) => item.id !== updated.id);
      this.applyVisibleItems();
      return;
    }

    const index = this.items.findIndex((item) => item.id === updated.id);
    if (index >= 0) {
      this.items[index] = updated;
      this.items = [...this.items];
      this.applyVisibleItems();
      return;
    }

    this.loadDevices();
  }

  private matchesSelectedFilters(item: DeviceItem): boolean {
    if (this.selectedStatus && item.status !== this.selectedStatus) {
      return false;
    }

    if (this.selectedClinicId && item.clinicId !== this.selectedClinicId) {
      return false;
    }

    if (this.selectedDeviceTypeId && item.deviceTypeId !== this.selectedDeviceTypeId) {
      return false;
    }

    const query = this.selectedQuery.trim().toLowerCase();
    if (!query) {
      return true;
    }

    return [
      item.serialNumber,
      item.brand,
      item.model,
      item.clinicName,
      item.deviceTypeName,
    ].some((value) => (value ?? '').toLowerCase().includes(query));
  }

  private applyVisibleItems(): void {
    this.hasRequiredSystemAdminFilters = !this.isSystemAdmin || Boolean(this.getCompanyScopeQuery());
    this.visibleItems = this.hasRequiredSystemAdminFilters ? [...this.items] : [];
  }

  private loadReferenceDataIfNeeded(): void {
    if (this.clinics.length > 0 && this.deviceTypes.length > 0) {
      return;
    }

    this.loadScopedData(this.getCompanyScopeQuery());
  }

  private loadScopedData(companyId?: string): void {
    if (this.isSystemAdmin && !companyId) {
      this.items = [];
      this.visibleItems = [];
      this.clinics = [];
      this.deviceTypes = [];
      this.createForm.patchValue({
        clinicId: '',
        deviceTypeValue: '',
      });
      this.applyVisibleItems();
      return;
    }

    forkJoin({
      clinics: this.settingsService.getClinicOperationalSettings(companyId),
      deviceTypes: this.deviceTypesService.getList(companyId),
    })
      .subscribe({
        next: ({ clinics, deviceTypes }) => {
          this.clinics = clinics;
          this.deviceTypes = deviceTypes;
          this.createForm.patchValue({
            clinicId: this.getSystemAdminClinicIdSeed(),
            deviceTypeValue: '',
          });
        },
        error: (error) => {
          this.actionErrorText = this.resolveActionError(error);
        },
      });
  }

  private resetScopeDependentState(): void {
    this.actionErrorText = null;
    this.modalActionErrorText = null;
    this.lifecycleErrorText = null;
    this.successText = null;
    this.isCreateModalOpen = false;
    this.isFiltersModalOpen = false;
    this.isClinicQuickViewOpen = false;
    this.selectedQuickViewClinicId = null;
    this.isDeviceTypeQuickViewOpen = false;
    this.selectedQuickViewDeviceTypeId = null;
    this.deviceModalMode = 'create';
    this.selectedStatus = '';
    this.selectedClinicId = '';
    this.selectedDeviceTypeId = '';
    this.selectedQuery = '';
    this.selectedSort = 'createdAtDesc';
    this.draftStatus = '';
    this.draftClinicId = '';
    this.draftDeviceTypeId = '';
    this.draftQuery = '';
    this.draftSort = 'createdAtDesc';
    this.nextCursor = null;
    this.hasMoreItems = false;
    this.clinics = [];
    this.deviceTypes = [];

    this.pendingPhotoRemove = null;
    this.pendingLifecycleDelete = null;
    this.pendingDeviceArchive = null;
    this.pendingDeviceDelete = null;
    this.editingLifecycleEventId = null;
    this.editPhotoFiles = [];
    this.lifecycleFiles = [];
    this.failedImageAttachments.clear();

    if (this.previewAttachment) {
      this.closeAttachmentPreview();
    }

    if (this.editingItem) {
      this.editingItem = null;
    }

    if (this.lifecycleDevice) {
      this.lifecycleDevice = null;
      this.lifecycleEvents = [];
      this.isLoadingLifecycle = false;
      this.isCreatingLifecycleEvent = false;
      this.isSavingLifecycleEdit = false;
    }
    this.isArchivingDevice = false;
    this.isDeletingDevice = false;
  }

  private afterCreateReset(): void {
    this.createForm.reset({
      clinicId: this.getSystemAdminClinicIdSeed(),
      deviceTypeValue: '',
      serialNumber: '',
      brand: '',
      model: '',
      manufactureDateText: '',
      lastServiceDateText: '',
      addedDateText: '',
      notes: '',
    });
    this.createPhotoFiles = [];
    this.loadDevices();
    this.loadDeviceTypes();
  }

  private loadDeviceTypes(): void {
    this.deviceTypesService.getList(this.getCompanyScopeQuery()).subscribe({
      next: (items) => {
        this.deviceTypes = items;
      },
      error: () => {
        // Keep current list if refresh fails; devices list still remains usable.
      },
    });
  }

  private toDateTimeLocal(value: string): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private getCompanyScopeQuery(): string | undefined {
    if (!this.isSystemAdmin) {
      return undefined;
    }

    return this.targetCompanyId?.trim() || undefined;
  }

  private getSystemAdminClinicIdSeed(): string {
    if (!this.isSystemAdmin) {
      return '';
    }

    return this.systemAdminContext.getClinicId()?.trim() ?? '';
  }

  private isDeviceStatus(value: string): value is DeviceStatus {
    return ['active', 'maintenance', 'broken', 'retired'].includes(value);
  }

  private isDeviceSort(value: string): value is DeviceListSort {
    return [
      'createdAtDesc',
      'createdAtAsc',
      'updatedAtDesc',
      'updatedAtAsc',
      'brandAsc',
      'brandDesc',
      'modelAsc',
      'modelDesc',
      'statusAsc',
      'statusDesc',
    ].includes(value);
  }

  protected get filteredCreateDeviceTypeOptions(): DeviceTypeItem[] {
    const query = this.createForm.controls.deviceTypeValue.value.trim().toLowerCase();
    if (!query) {
      return this.deviceTypes;
    }

    return this.deviceTypes.filter((item) => item.name.toLowerCase().includes(query));
  }

  protected get filteredEditDeviceTypeOptions(): DeviceTypeItem[] {
    const query = this.editForm.controls.deviceTypeValue.value.trim().toLowerCase();
    if (!query) {
      return this.deviceTypes;
    }

    return this.deviceTypes.filter((item) => item.name.toLowerCase().includes(query));
  }

  private resolveDeviceTypePayload(value: string): {
    deviceTypeId?: string;
    deviceTypeName?: string;
  } {
    const normalized = value.trim();
    if (!normalized) {
      return {};
    }

    const existing = this.deviceTypes.find(
      (item) => item.name.trim().toLowerCase() === normalized.toLowerCase(),
    );

    if (existing) {
      return { deviceTypeId: existing.id };
    }

    return { deviceTypeName: normalized };
  }

  protected get hasCompanyScopeSelected(): boolean {
    return Boolean(this.getCompanyScopeQuery());
  }

  protected get hasSystemAdminClinicScopeSelected(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }

    return Boolean(this.getSystemAdminClinicIdSeed());
  }

  private get hasSystemAdminContextCompanySelected(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }
    return Boolean(this.systemAdminContext.getCompanyId()?.trim());
  }

  private get hasSystemAdminContextClinicSelected(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }
    return Boolean(this.systemAdminContext.getClinicId()?.trim());
  }

  protected hasCreateDeviceTypeSelection(): boolean {
    const value = this.createForm.getRawValue();
    return Boolean(value.deviceTypeValue.trim());
  }

  protected canSubmitCreate(): boolean {
    if (this.isCreating || this.isUploadingPhotos) {
      return false;
    }

    if (!this.hasCreateDeviceTypeSelection()) {
      return false;
    }

    if (this.isSystemAdmin) {
      return this.createForm.controls.deviceTypeValue.valid
        && this.createForm.controls.serialNumber.valid
        && this.createForm.controls.brand.valid
        && this.createForm.controls.model.valid
        && this.createForm.controls.manufactureDateText.valid
        && this.createForm.controls.lastServiceDateText.valid
        && this.createForm.controls.addedDateText.valid
        && this.hasSystemAdminContextCompanySelected
        && this.hasSystemAdminContextClinicSelected;
    }

    return this.createForm.valid && Boolean(this.createForm.controls.clinicId.value.trim());
  }

  protected get createSubmitBlockedReason(): string | null {
    if (this.isDeviceModalEditMode || this.isCreating || this.isUploadingPhotos) {
      return null;
    }

    if (this.isSystemAdmin && !this.hasSystemAdminContextCompanySelected) {
      return 'error.settingsCompanyContextRequired';
    }

    if (this.isSystemAdmin && !this.hasSystemAdminContextClinicSelected) {
      return 'Select clinic in global header.';
    }

    if (!this.hasCreateDeviceTypeSelection()) {
      return 'Select device type or enter custom value.';
    }

    if (!this.createForm.controls.serialNumber.valid) {
      return 'Serial Number is required.';
    }
    if (!this.createForm.controls.brand.valid) {
      return 'Brand is required.';
    }
    if (!this.createForm.controls.model.valid) {
      return 'Model is required.';
    }
    if (!this.createForm.controls.manufactureDateText.valid) {
      return 'Manufacture Date is required.';
    }
    if (!this.createForm.controls.lastServiceDateText.valid) {
      return 'Last Service Date is required.';
    }
    if (!this.createForm.controls.addedDateText.valid) {
      return 'Added Date is required.';
    }

    if (!this.isSystemAdmin && !this.createForm.controls.clinicId.value.trim()) {
      return 'Select clinic.';
    }

    return null;
  }

  protected canOpenCreateModal(): boolean {
    if (this.isCreating || this.isUploadingPhotos) {
      return false;
    }

    if (!this.isSystemAdmin) {
      return true;
    }

    return this.hasSystemAdminContextCompanySelected && this.hasSystemAdminContextClinicSelected;
  }

  protected get createOpenBlockedReason(): string | null {
    if (!this.isSystemAdmin || this.canOpenCreateModal()) {
      return null;
    }

    if (!this.hasSystemAdminContextCompanySelected) {
      return 'error.settingsCompanyContextRequired';
    }

    if (!this.hasSystemAdminContextClinicSelected) {
      return 'Select clinic in global header.';
    }

    return null;
  }

  protected get systemAdminHeaderScopeReason(): string | null {
    if (!this.isSystemAdmin) {
      return null;
    }

    if (!this.hasRequiredSystemAdminFilters) {
      return 'error.settingsCompanyContextRequired';
    }

    return this.createOpenBlockedReason;
  }

  protected canSubmitDeviceModal(): boolean {
    if (this.isDeviceModalViewMode) {
      return false;
    }
    if (this.isDeviceModalEditMode) {
      return !(this.editForm.invalid || this.isSavingEdit || this.isUploadingPhotos || !this.hasEditDeviceTypeSelection());
    }
    return this.canSubmitCreate();
  }

  protected hasEditDeviceTypeSelection(): boolean {
    const value = this.editForm.getRawValue();
    return Boolean(value.deviceTypeValue.trim());
  }
}
